import React, { useState, useEffect } from "react";
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Typography,
  Button,
  useTheme,
  CircularProgress,
  Menu,
  MenuItem,
  Tooltip,
} from "@mui/material";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import { useSelector } from "react-redux";
import VantageListHead from "../../component/ListHead/SignalList";
import { TABLE_HEAD_SIGNAL_HISTORICAL, applySortFilter, getComparator } from "../../component/Methods";
import { toast } from "react-toastify";
import { selectHistoricalSignalSet ,selectActiveRequests} from "../../Redux/Reducers/selectors";
import HistoricalSignalsSummary from "./HistoricalSignalsSummary";
const HistoricalSignalsList = ({
  strategies,
  strategyValObj,
  strategyVal,
}) => {
  const theme = useTheme();
  const [historicalData, setHistoricalData] = useState([]);
  const [order, setOrder] = useState("desc");
  const [orderBy, setOrderBy] = useState("value");
  const [copyMenuAnchor, setCopyMenuAnchor] = useState(null);
  const [copyMenuOpen, setCopyMenuOpen] = useState(false);
  const [currentSignalSetIndex, setCurrentSignalSetIndex] = useState(null);

  const historicalSignalSet = useSelector(selectHistoricalSignalSet);
  const activeRequests = useSelector(selectActiveRequests);
  const isFetchingHistoricalSignals = activeRequests?.includes("get_historical_signal_set") || false;


  useEffect(() => {
    if (historicalSignalSet && historicalSignalSet.signalSets && Array.isArray(historicalSignalSet.signalSets) && strategyValObj) {
      // Filter by selected strategy
      const filteredData = historicalSignalSet.signalSets.filter(
        (signal) => signal.strategy === strategyValObj
      );

      if (filteredData.length > 0) {
        // Sort by date (newest first) and process all signal sets
        const sortedData = filteredData.sort((a, b) => b.date - a.date);
        const allSignals = sortedData.map(signalSet => ({
          ...signalSet,
          processedSignals: applySortFilter(
            processHistoricalSignals(signalSet),
            getComparator(order, orderBy)
          )
        }));
        setHistoricalData(allSignals);
      } else {
        setHistoricalData([]);
      }
    } else {
      setHistoricalData([]);
    }
  }, [historicalSignalSet, strategyValObj, order, orderBy]);



  const processHistoricalSignals = (signalData) => {
    if (!signalData || !signalData.weights_keys || !signalData.weights_values) {
      return [];
    }

    const signals = [];
    const { weights_keys, weights_values, performance_keys, performance_values } = signalData;

    Object.keys(weights_values).forEach(key => {
      const weight = weights_values[key];
      const tickerInfo = weights_keys[key];
      
      if (tickerInfo && tickerInfo.symbol) {
        // Get performance data for this ticker
        let performance = null;
        if (performance_keys && performance_values) {
          // Find matching performance key for this ticker
          const perfKey = Object.keys(performance_keys).find(perfKey => {
            const perfTickerInfo = performance_keys[perfKey];
            return perfTickerInfo && perfTickerInfo.symbol && 
                   perfTickerInfo.symbol.toUpperCase() === tickerInfo.symbol.toUpperCase();
          });
          
          if (perfKey && performance_values[perfKey] !== undefined) {
            performance = performance_values[perfKey];
          }
        }

        signals.push({
          key,
          name: tickerInfo.symbol.toUpperCase(),
          value: (weight * 100).toFixed(4),
          long_short: weight >= 0 ? "L" : "S",
          performance: performance !== null ? (performance * 100).toFixed(2) : null,
        });
      }
    });

    // Return unsorted signals - sorting will be handled by applySortFilter
    return signals;
  };

  const handleRequestSort = (event, property) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const formatDate = (timestamp) => {
    const date = new Date(timestamp);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const handleCopyMenuOpen = (event, signalSetIndex) => {
    setCopyMenuAnchor(event.currentTarget);
    setCopyMenuOpen(true);
    setCurrentSignalSetIndex(signalSetIndex);
  };

  const handleCopyMenuClose = () => {
    setCopyMenuOpen(false);
    setCopyMenuAnchor(null);
    setCurrentSignalSetIndex(null);
  };

  const handleCopyForWebsite = () => {
    if (currentSignalSetIndex !== null && historicalData[currentSignalSetIndex]) {
      const signals = historicalData[currentSignalSetIndex].processedSignals;
      if (!signals || signals.length === 0) {
        toast.error("No signals to copy");
        return;
      }

      try {
        const jsonData = JSON.stringify(signals);
        navigator.clipboard.writeText(jsonData);
      } catch (error) {
        console.error("Error copying signals:", error);
        toast.error("Failed to copy signals");
      }
    }
    handleCopyMenuClose();
  };

  const handleCopyForExcel = () => {
    if (currentSignalSetIndex !== null && historicalData[currentSignalSetIndex]) {
      const signals = historicalData[currentSignalSetIndex].processedSignals;
      if (!signals || signals.length === 0) {
        toast.error("No signals to copy");
        return;
      }

      try {
        let formattedData = signals
          .map((item) => `${item.name}\t${item.value}%`)
          .join("\n");
        navigator.clipboard.writeText(formattedData);
      } catch (error) {
        console.error("Error copying signals:", error);
        toast.error("Failed to copy signals");
      }
    }
    handleCopyMenuClose();
  };

  if (!strategyVal) {
    return (
      <Box
        sx={{
          textAlign: "center",
          py: 8,
          color: theme.palette.text.secondary,
        }}
      >
        <Typography variant="h5">
          Please select a strategy to view historical signals
        </Typography>
      </Box>
    );
  }

  if (isFetchingHistoricalSignals) {
    return (
      <Box
        sx={{
          textAlign: "center",
          py: 8,
          color: theme.palette.text.secondary,
        }}
      >
        <CircularProgress size={40} sx={{ mb: 2 }} />
        <Typography variant="h5">
          Fetching historical signals...
        </Typography>
      </Box>
    );
  }

  if (!historicalData || historicalData.length === 0) {
    return (
      <Box
        sx={{
          textAlign: "center",
          py: 8,
          color: theme.palette.text.secondary,
        }}
      >
        <Typography variant="h5">
          No historical signals found for the selected strategy
        </Typography>
      </Box>
    );
  }

  return (
    <>
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "repeat(3, 1fr)",
          gap: 3,
          width: "100%",
        }}
      >
        {historicalData.map((signalSet, signalSetIndex) => (
          <Box
            key={signalSetIndex}
            sx={{
              borderRadius: "4px",
              border: `1px solid ${theme.palette.divider}`,
              backgroundColor: theme.palette.background.paper,
              overflow: "hidden",
            }}
          >
            {/* Header with Date and Copy Button */}
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                p: 2,
                backgroundColor: theme.palette.background.default,
                borderBottom: `1px solid ${theme.palette.divider}`,
              }}
            >
              <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                {formatDate(signalSet.date)}
              </Typography>
              <Tooltip title="Copy Signals" arrow placement="top">
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<ContentCopyIcon />}
                  onClick={(event) => handleCopyMenuOpen(event, signalSetIndex)}
                  sx={{
                    color: theme.palette.primary.main,
                    borderColor: theme.palette.primary.main,
                    fontWeight: 500,
                    "&:hover": {
                      borderColor: theme.palette.primary.dark,
                      backgroundColor: theme.palette.primary.light + "20",
                      transform: "scale(1.02)",
                    },
                    transition: "all 0.2s ease-in-out",
                  }}
                >
                  Copy
                </Button>
              </Tooltip>
            </Box>

            {/* Content Area with Summary and Table */}
            <Box sx={{ p: 2 }}>
              <HistoricalSignalsSummary 
                signalData={signalSet} 
                isLatestSignal={signalSetIndex === 0}
                nextSignalDate={signalSetIndex > 0 ? historicalData[signalSetIndex - 1].date : null}
              />
              
              <TableContainer style={{ height: "400px", marginTop: "8px" }}>
                <Table
                  size="small"
                  stickyHeader
                  aria-label="a dense table"
                  sx={{
                    height: "max-content",
                  }}
                >
                  <VantageListHead
                    order={order}
                    orderBy={orderBy}
                    headLabel={TABLE_HEAD_SIGNAL_HISTORICAL}
                    onRequestSort={handleRequestSort}
                    noStatus={true}
                    customizeFont={12}
                  />
                  <TableBody>
                    {signalSet.processedSignals?.map((row, index) => (
                      <TableRow
                        key={row?.key}
                        sx={{
                          overflow: "auto",
                          "&:hover": {
                            background: theme.palette.grey[200],
                            cursor: "pointer",
                          },
                          "& > .MuiTableCell-root": {
                            paddingTop: "1px",
                            paddingBottom: "0px !important",
                            lineHeight: "1.2",
                          },
                        }}
                      >
                        <TableCell
                          align="left"
                          sx={{
                            fontSize: "12px",
                            paddingLeft: "5px",
                            color: theme.palette.text.primary,
                            borderRight: "none",
                            cursor: "pointer",
                          }}
                        >
                          {row?.name}
                        </TableCell>
                        <TableCell
                          align="right"
                          sx={{
                            fontSize: "12px",
                            paddingLeft: "5px",
                            color: theme.palette.text.primary,
                            borderLeft: "none",
                            borderRight: "none",
                          }}
                        >
                          {row?.value}%
                        </TableCell>
                        <TableCell
                          align="left"
                          sx={{
                            fontSize: "12px",
                            paddingLeft: "10px",
                            color: theme.palette.text.primary,
                            borderLeft: "none",
                            height: "21.5px",
                          }}
                        >
                          {row?.long_short}
                        </TableCell>
                       
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          </Box>
        ))}
      </Box>

      {/* Copy Menu */}
      <Menu
        anchorEl={copyMenuAnchor}
        open={copyMenuOpen}
        onClose={handleCopyMenuClose}
        MenuListProps={{
          "aria-labelledby": "copy-button",
        }}
      >
        <MenuItem onClick={handleCopyForWebsite}>
          Copy for Website
        </MenuItem>
        <MenuItem onClick={handleCopyForExcel}>
          Copy for Excel
        </MenuItem>
      </Menu>
    </>
  );
};

export default HistoricalSignalsList;
